import { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  css,
  Stack,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { AU_STATES, US_STATES } from './constants';
import { staticFileLocation } from '@server/constants';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { WotcSurveyData } from './types';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useUserCountry } from '@context/UserCountryContext';

type VeteranSurveyFormProps = {
  veteranFormModal: boolean;
  wotcSurveyData: WotcSurveyData;
  handleClose: () => void;
};

const defaultValues = {
  branch: '',
  armed1: 'Y',
  armed1a: '',
  armed1b: null as Dayjs | null,
  armed1c: null as Dayjs | null,
  snap1_recipient_first_name: '',
  snap1_recipient_last_name: '',
  snap1_recipient_city: '',
  snap1_recipient_state: 'AK',
  armed1d: '',
  armed1e: '',
  armed1f: 'Y',
  armed1f_recipient_first_name: '',
  armed1f_recipient_last_name: '',
  armed1f_recipient_city: '',
  armed1f_recipient_state: '',
  armed1g: '',
  armed1ga: '',
};

const VeteranSurveyForm: FC<VeteranSurveyFormProps> = ({
  veteranFormModal,
  handleClose,
  wotcSurveyData,
}) => {
  const { userCountry } = useUserCountry();
  const states = userCountry === 'au' ? AU_STATES : US_STATES;

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: 'onChange', // Validate on change
  });
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const watchArmed1 = watch('armed1');

  const onSubmit = (data: any) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };
    const transformedData = {
      // ...wotcSurveyData,
      ...data,
      armed1b: data.armed1b
        ? data.armed1b.toISOString().split('T')[0]
        : undefined,
      armed1c: data.armed1c
        ? data.armed1c.toISOString().split('T')[0]
        : undefined,
      credit: data.armed1 === 'Y' ? 9600 : 2400,
      status: 1,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (typedKey === 'armed1' && wotcSurveyData[typedKey] === null) {
            setValue('armed1', 'Y');
          } else if (typedKey === 'armed1b' || typedKey === 'armed1c') {
            const dateValue = wotcSurveyData[typedKey];
            const date = dateValue ? dayjs(dateValue) : null;
            setValue(typedKey, date);
          } else if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={veteranFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <img
            src={`${staticFileLocation}img/images/icon1.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">Veteran Information</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2 }}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          <>
            <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
              <Box
                mb={3}
                sx={{
                  display: 'flex',
                  alignItems: {
                    xs: 'start',
                    sm: 'center',
                  },
                  justifyContent: 'space-between',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                }}
              >
                <FormLabel component="legend">
                  Are you a SNAP veteran?
                </FormLabel>
                <Controller
                  name="armed1f"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                      <FormControlLabel
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                      <FormControlLabel
                        value="U"
                        control={<Radio />}
                        label="Unsure"
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
            </FormControl>
            {/* First Name */}
            <Stack
              spacing={2}
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              mb={3}
            >
              <Controller
                name="snap1_recipient_first_name"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Enter the first name of the primary recipient*"
                    fullWidth
                    size="small"
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              {/* Last Name */}
              <Controller
                name="snap1_recipient_last_name"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Enter the last name of the recipient*"
                    fullWidth
                    size="small"
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>

            {/* City */}
            <Stack
              spacing={2}
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              mb={3}
            >
              <Controller
                name="snap1_recipient_city"
                control={control}
                rules={{ required: 'City is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Enter the city where the benefits were received*"
                    fullWidth
                    size="small"
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              {/* State */}
              <Controller
                name="snap1_recipient_state"
                control={control}
                rules={{ required: 'State is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    label="Enter the state where benefits were received*"
                    fullWidth
                    margin="normal"
                  >
                    {states.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          </>
          {/* Are you a veteran of the US Armed Forces? */}
          <FormControl fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
              }}
            >
              <FormLabel>Are you a veteran of the US Armed Forces?</FormLabel>
              <Controller
                name="armed1"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
            <Typography color="error" variant="body2">
              {errors.armed1?.message}
            </Typography>
          </FormControl>

          {watchArmed1 === 'Y' && (
            <>
              {/* Branch */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel>Which branch did you serve in?</FormLabel>
                <Controller
                  name="armed1a"
                  control={control}
                  rules={{ required: 'Branch is required' }}
                  render={({ field }) => (
                    <Select {...field} fullWidth size="small">
                      <MenuItem value="" selected disabled>
                        Select the branch of the Military
                      </MenuItem>
                      <MenuItem value="Army">Army</MenuItem>
                      <MenuItem value="Navy">Navy</MenuItem>
                      <MenuItem value="Coast Guard">Coast Guard</MenuItem>
                      <MenuItem value="Air Force">Air Force</MenuItem>
                      <MenuItem value="National Guard">National Guard</MenuItem>
                      <MenuItem value="Marines">Marines</MenuItem>
                      <MenuItem value="Space Force">Space Force</MenuItem>
                    </Select>
                  )}
                />
                <Typography color="error" variant="body2">
                  {errors.armed1a?.message}
                </Typography>
              </FormControl>

              {/* Enlistment Date */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Controller
                    name="armed1b"
                    control={control}
                    rules={{
                      required: 'Enlistment date is required',
                      // validate: (value) =>
                      //   value instanceof Date || 'Invalid date',
                    }}
                    render={({ field }) => (
                      <DatePicker
                        label="Enlistment Date*"
                        format="MM/DD/YYYY"
                        sx={{ width: '100%' }}
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            sx: { mb: 3 },
                            error: !!errors.armed1b,
                            helperText: errors.armed1b?.message,
                          },
                        }}
                      />
                    )}
                  />
                  {errors.armed1b && (
                    <Typography
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.armed1b?.message}
                    </Typography>
                  )}
                </Box>

                {/* Discharge Date */}
                <Box sx={{ width: '100%' }}>
                  <Controller
                    name="armed1c"
                    control={control}
                    rules={{
                      required: 'Discharge date is required',
                    }}
                    render={({ field }) => (
                      <DatePicker
                        label="Discharge Date*"
                        format="MM/dd/yyyy"
                        sx={{ width: '100%' }}
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            sx: { mb: 3 },
                            error: !!errors.armed1c,
                            helperText: errors.armed1c?.message,
                          },
                        }}
                      />
                    )}
                  />
                  {errors.armed1c && (
                    <Typography
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.armed1c?.message}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Unemployed for combined 6 months */}
              <FormControl fullWidth sx={{ mt: 3, mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: {
                      xs: 'start',
                      sm: 'center',
                    },
                    justifyContent: 'space-between',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: '4px',
                  }}
                >
                  <FormLabel>
                    Were you unemployed for a combined period of at least 6
                    months (whether or not consecutive) during the previous
                    year?
                  </FormLabel>
                  <Controller
                    name="armed1d"
                    control={control}
                    rules={{ required: 'This field is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
                <Typography color="error" variant="body2">
                  {errors.armed1d?.message}
                </Typography>
              </FormControl>

              {watchArmed1 === 'Y' && (
                <>
                  {/* Unemployed for 4–6 months */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: {
                          xs: 'start',
                          sm: 'center',
                        },
                        justifyContent: 'space-between',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        gap: '4px',
                      }}
                    >
                      <FormLabel>
                        Were you unemployed for a combined period of at least 4
                        weeks but less than 6 months during the previous year?
                      </FormLabel>
                      <Controller
                        name="armed1e"
                        control={control}
                        rules={{ required: 'This field is required' }}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            row
                            sx={{ flexWrap: 'nowrap' }}
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    <Typography color="error" variant="body2">
                      {errors.armed1e?.message}
                    </Typography>
                  </FormControl>
                </>
              )}

              {watchArmed1 === 'Y' && (
                <>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: {
                          xs: 'start',
                          sm: 'center',
                        },
                        justifyContent: 'space-between',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        gap: '4px',
                      }}
                    >
                      <FormLabel>
                        Check here if you are a veteran entitled to compensation
                        for a service-connected disability and you were
                        discharged or released from active duty in the U.S.
                        Armed Forces during the past year.
                      </FormLabel>
                      <Controller
                        name="armed1g"
                        control={control}
                        rules={{ required: 'This field is required' }}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            row
                            sx={{ flexWrap: 'nowrap' }}
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    <Typography color="error" variant="body2">
                      {errors.armed1g?.message}
                    </Typography>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: {
                          xs: 'start',
                          sm: 'center',
                        },
                        justifyContent: 'space-between',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        gap: '8px',
                      }}
                    >
                      <FormLabel>
                        Check here if you are a veteran entitled to compensation
                        for a service-connected disability and you were
                        unemployed for a period or periods totaling at least 6
                        months during the past year.
                      </FormLabel>
                      <Controller
                        name="armed1ga"
                        control={control}
                        rules={{ required: 'This field is required' }}
                        render={({ field }) => (
                          <RadioGroup
                            {...field}
                            row
                            sx={{ flexWrap: 'nowrap' }}
                          >
                            <FormControlLabel
                              value="Y"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="N"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    <Typography color="error" variant="body2">
                      {errors.armed1ga?.message}
                    </Typography>
                  </FormControl>
                </>
              )}
            </>
          )}

          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ width: '100%' }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VeteranSurveyForm;
